import { AnyAction } from 'redux';
import { SagaIterator } from 'redux-saga';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import { CallApiResult, IF_MATCH_HEADER, IF_MODIFIED_SINCE_HEADER, RequestHeaders } from 'src/api';
import { create, createBatch, deleteById, fetchAll, fetchById, patch, search } from 'src/api/entities';
import { createSharedRequests } from 'src/core/sharedRequests';
import { CREATE, DELETE, FETCH, UPDATE } from 'src/store/entities/actions';
import { getEntityById } from 'src/store/entities/selectors';
import EntityDescription, { Projection } from 'src/store/entities/types/EntityDescription';
import { SingleEntityState } from 'src/store/entities/types/SingleEntityState';

/**
 * Generates a saga that fetches item(s) for the given entityDescription
 * and dispatch the appropriate actions.
 *
 * If action.id is present it will fetch a single item by id.
 * Otherwise all entities will be fetched.
 */
const collectionSequences: Record<string, number> = {};

// identical collection reads in flight share one request (the shop page fired /api/products 4×)
const collectionRequests = createSharedRequests<CallApiResult>();

function collectionRequestKey(
    entityDescription: EntityDescription,
    searchMethod: string | null,
    projection: Projection | undefined,
    headers: RequestHeaders | undefined
): string {
    const common = axios.defaults.headers.common as Record<string, unknown>;
    return JSON.stringify([
        entityDescription.name,
        searchMethod,
        projection ?? null,
        headers ?? {},
        common.language ?? null,
        common.region ?? null,
        common.customerId ?? null,
        common.Authorization ?? null,
    ]);
}

function nextCollectionSequence(entityDescription: EntityDescription): number {
    const next = (collectionSequences[entityDescription.name] ?? 0) + 1;
    collectionSequences[entityDescription.name] = next;
    return next;
}

function isLatestCollectionRequest(entityDescription: EntityDescription, sequence: number): boolean {
    return collectionSequences[entityDescription.name] === sequence;
}

export function* fetchSaga(entityDescription: EntityDescription, action: AnyAction): SagaIterator {
    yield put(entityDescription.actions.request(action.projection));

    const id = action.id ? action.id : action.data && action.data.id ? action.data.id : null;
    const searchMethod = action.searchMethod ? action.searchMethod : null;
    const headers: RequestHeaders = action.headers;

    let result: CallApiResult;

    // fetch single entity by id
    if (id) {
        // populate headers if entity already exists in local redux store
        // a list item in the store is only a partial copy — a forced refresh skips the conditional
        // request so the server answers with the full entity instead of 304
        const entity: SingleEntityState<any> = yield call(getEntity, entityDescription, id, action.projection);
        if (entity.content != null && !action.forceRefresh)
            headers[IF_MODIFIED_SINCE_HEADER] = entity.content.modifiedAt.toISOString();

        result = yield call(fetchById, entityDescription, id, action.projection, headers);

        if (result.error) {
            // entity did not change
            if (result.error.code === 304) yield put(entityDescription.actions.notModified(action.projection));
            // error occurred
            else {
                yield put(entityDescription.actions.failure(result.error, action.projection));
            }
        } else yield put(entityDescription.actions.successFetch(result.response, action.projection));
    } else {
        // either fetch all entities or by the given search query
        const sequence = nextCollectionSequence(entityDescription);
        result = yield call(
            [collectionRequests, collectionRequests.get],
            collectionRequestKey(entityDescription, searchMethod, action.projection, headers),
            () =>
                searchMethod
                    ? search(entityDescription, searchMethod, action.projection, headers)
                    : fetchAll(entityDescription, action.projection, headers)
        );
        // a later collection request for this entity has been issued meanwhile — its answer wins,
        // an out-of-order response must not overwrite it (shop: "all products" landing on a category)
        if (!isLatestCollectionRequest(entityDescription, sequence)) return;
        if (result.error) {
            yield put(entityDescription.actions.failure(result.error, action.projection));
        } else {
            yield put(entityDescription.actions.successFetch(result.response, action.projection));
        }
    }
}

/**
 * Generates a create or patch saga for the given entityDescription depending on the method
 * After a successful create or patch the entity will be fetched with the given projection
 */
function* createPatchEntitySaga(
    entityDescription: EntityDescription,
    method: typeof CREATE | typeof UPDATE,
    action: AnyAction
): SagaIterator {
    const headers: RequestHeaders = {};

    const isBodyArray = Array.isArray(action.data);

    let apiFn;
    switch (method) {
        case CREATE:
            apiFn = isBodyArray ? createBatch : create;
            break;
        case UPDATE:
            apiFn = patch;
            if (action.data.id) {
                const entity: SingleEntityState<any> = yield call(
                    getEntity,
                    entityDescription,
                    action.data.id,
                    action.projection
                );
                if (entity.content != null) headers[IF_MATCH_HEADER] = entity.content.version.toString();
            }
            break;
        default:
            throw new Error('createPatchEntitySaga(): Unknown method: ' + method);
    }

    yield put(entityDescription.actions.request(action.projection));

    const { error, response } =
        action.patchMethod != null
            ? yield call(apiFn, entityDescription, action.data, action.projection, headers, action.patchMethod)
            : yield call(apiFn, entityDescription, action.data, action.projection, headers);

    if (error) {
        yield put(entityDescription.actions.failure(error, action.projection));
    } else {
        // if entity has been created -> set id of new entity in action in order to fetch it with a projection
        if (method === CREATE && response && response.data && !isBodyArray) action.id = response.data.id;

        // batch create instantly returns the results with the correct projection
        if (isBodyArray) yield put(entityDescription.actions.successFetch(response, action.projection, true));
        // single create needs to fetch the created entity with the correct projection
        else yield call(fetchSaga, entityDescription, action);
    }
}

/**
 * Generates a delete saga the deletes an entity by id.
 */
function* deleteEntitySaga(entityDescription: EntityDescription, action: AnyAction): SagaIterator {
    const headers: RequestHeaders = {};

    // populate headers if entity exist in local redux store
    const entity: SingleEntityState<any> = yield call(getEntity, entityDescription, action.id, action.projection);
    if (entity.content != null) headers[IF_MATCH_HEADER] = entity.content.version.toString();

    yield put(entityDescription.actions.request(action.projection));

    const { error } = yield call(deleteById, entityDescription, action.id, headers);

    if (error) {
        yield put(entityDescription.actions.failure(error, action.projection));
    } else {
        yield put(entityDescription.actions.successDelete({ id: action.id }, action.projection));
    }
}

/** Get an entity from the store */
function* getEntity(entityDescription: EntityDescription, id: string, projection: Projection) {
    return yield select(getEntityById, entityDescription, id, projection);
}

/******************************************************************************/
/******************************* WATCHERS *************************************/
/******************************************************************************/

/**
 * Fetch Watcher for the given entityDescription
 */
export function* generateFetchWatcherByEntity(entityDescription: EntityDescription) {
    yield takeEvery(`${entityDescription.name}_${FETCH}`, fetchSaga, entityDescription);
}

/**
 * Create Watcher for the given entityDescription
 */
export function* generateCreateEntityWatcher(entityDescription: EntityDescription) {
    yield takeLatest(`${entityDescription.name}_${CREATE}`, createPatchEntitySaga, entityDescription, CREATE);
}

/**
 * Patch Watcher for the given entityDescription
 */
export function* generateUpdateEntityWatcher(entityDescription: EntityDescription) {
    yield takeLatest(`${entityDescription.name}_${UPDATE}`, createPatchEntitySaga, entityDescription, UPDATE);
}

/**
 * Delete Watcher for the given entityDescription
 */
export function* generateDeleteEntityWatcher(entityDescription: EntityDescription) {
    yield takeLatest(`${entityDescription.name}_${DELETE}`, deleteEntitySaga, entityDescription);
}
